/**
 * Processeur de messages WebSocket
 * Traitement IMMÉDIAT sans timeout artificiel
 */

import AudioLib from '@/services/audio-lib';
import { getBallLetter, getLastBallId } from '@/utils/bingo';
import { showMessage } from '@/utils/chatClient';
import { playAudio } from './audio-handler.js';
import { GAME_ACTIONS, GAME_MESSAGES } from './constants.js';
import { canUpdateIncrementally, updateGameState } from './state-updater.js';

// Note: updateGameState et canUpdateIncrementally sont importés pour la mise à jour après boule frimée

/**
 * Traite un message WebSocket reçu
 * @param {string} rawMessage - Message JSON brut du WebSocket
 * @param {Object} callbacks - Objet contenant les callbacks pour mettre à jour l'UI
 * @returns {Object} Résultat du traitement avec les actions à effectuer
 */
export function processMessage(rawMessage, callbacks) {
  try {
    // Parser le message immédiatement
    const gameData = JSON.parse(rawMessage);
    gameData.audioMessage = gameData.message;

    // Log simple du type de message avec timestamp pour détecter les doublons
    const timestamp = new Date().toISOString();
    console.info('📨 Message reçu - Action:', gameData.action, '| Message:', gameData.message, '| Value:', gameData.value, '| Time:', timestamp);

    // Résultat du traitement
    let result = {
      needsFullRefresh: false,
      updatedModel: null,
      audioToPlay: null,
      messageToDisplay: null,
      navigation: null,
    };

    // Traiter selon le type d'action
    switch (gameData.action) {
      case GAME_ACTIONS.BALL:
        result = handleBallAction(gameData, callbacks);
        break;

      case GAME_ACTIONS.MESSAGE:
        result = handleMessageAction(gameData, callbacks);
        break;

      case GAME_ACTIONS.MESSAGE_PERSONNALISE:
        result = handlePersonalizedMessage(gameData, callbacks);
        break;

      case GAME_ACTIONS.WINNER:
        result = handleWinnerAction(gameData, callbacks);
        break;

      case GAME_ACTIONS.UPDATE_WINNER:
        result = handleUpdateWinnerAction(gameData, callbacks);
        break;

      case 'none':
        // Message de synchronisation/reset - refetch le modèle
        console.info('🔄 Action "none" détectée - Refetch du modèle');
        result.needsFullRefresh = true;
        result.refreshDelay = 500; // Petit délai pour laisser la DB se mettre à jour
        break;

      default:
        result = handleDefaultAction(gameData, callbacks);
        break;
    }

    return result;
  } catch (error) {
    console.error('Erreur lors du traitement du message:', error);
    return {
      needsFullRefresh: false,
      error: error.message,
    };
  }
}

/**
 * Traite l'action "ball" (boule tirée)
 */
function handleBallAction(gameData, callbacks) {
  const result = {
    needsFullRefresh: false,
    updatedModel: null,
    audioToPlay: null,
    messageToDisplay: {
      message: '',
      show: false
    },
  };

  // Extraire la dernière boule tirée
  if (gameData.allBalls) {
    const lastBallIdString = getLastBallId(gameData.allBalls);
    const lastBallId = parseInt(lastBallIdString);
    const ballLetter = getBallLetter(lastBallId).toUpperCase();
    const ballName = ballLetter + lastBallIdString;

    // Jouer l'audio immédiatement
    playAudio(ballName);
    result.audioToPlay = ballName;

    // Mettre à jour le modèle de façon incrémentale
    if (callbacks.currentModel && canUpdateIncrementally(GAME_ACTIONS.BALL)) {
      result.updatedModel = updateGameState(callbacks.currentModel, gameData);
      console.info('Modèle mis à jour incrémentalement. Nombre de boules:', result.updatedModel?.sequenceActivities?.length);
    } else {
      console.warn('Impossible de mettre à jour incrémentalement - currentModel:', !!callbacks.currentModel, 'canUpdate:', canUpdateIncrementally(GAME_ACTIONS.BALL));
    }
  }

  return result;
}

/**
 * Traite l'action "message" (messages système)
 */
function handleMessageAction(gameData) {
  const result = {
    needsFullRefresh: false,
    updatedModel: null,
    audioToPlay: null,
    messageToDisplay: null,
    navigation: null,
  };

  const messageId = gameData.message;

  switch (messageId) {
    case GAME_MESSAGES.GAME_ENDED:
      // Fin de partie - nécessite un refresh complet
      console.info('🏁 GAME_ENDED - Value:', gameData.value);
      playAudio(messageId);
      result.audioToPlay = messageId;

      if (gameData.value === 'fin') {
        // Navigation après 10 secondes (comme avant)
        console.info('🏁 Fin du tirage - Navigation vers / dans 10s');
        result.needsFullRefresh = true;
        result.navigation = {
          path: '/',
          delay: 10000,
        };
      } else {
        // Sinon, c'est la fin d'un tour, on doit refetch le modèle pour le prochain tour
        // Mais on attend un peu pour que la DB ait le temps de se mettre à jour
        const nextSequence = parseInt(gameData.value) || null;
        console.info('🏁 Fin du tour - Refetch du modèle pour le prochain tour (séquence:', nextSequence, ') - Délai de 1000ms');
        result.needsFullRefresh = true;
        result.refreshDelay = 1000; // Délai de 1000ms pour laisser la DB se mettre à jour
        result.expectedSequence = nextSequence; // Pour vérifier que le modèle a bien changé
      }
      break;

    case GAME_MESSAGES.TRANSCRIPTION_ERROR:
      // Erreur de transcription - nécessite un refresh complet
      playAudio(messageId);
      result.needsFullRefresh = true;
      result.audioToPlay = messageId;
      result.messageToDisplay = {
        message: `ATTENTION LA BOULE ${gameData.value} A ÉTÉ RETIRÉE`,
        status: 'warning',
        show: true,
      };
      break;

    case GAME_MESSAGES.JOKER_BALL:
      // Boule frimée - séquence audio spéciale
      playAudio(GAME_MESSAGES.JOKER_BALL);

      // Programmer les autres sons (comme avant)
      setTimeout(() => {
        playAudio(gameData.value);
      }, 4000);

      setTimeout(() => {
        playAudio(GAME_MESSAGES.UPDATE_CARD);
      }, 9000);

      result.audioToPlay = GAME_MESSAGES.JOKER_BALL;
      result.messageToDisplay = {
        message: AudioLib.messages.joker_ball.message,
        status: 'warning',
        show: true,
      };

      // Indiquer qu'il faut mettre à jour le modèle après 4 secondes avec la boule réelle
      // pour que les cartes s'ajustent (comme setBallSelected dans l'ancien code)
      if (gameData.allBalls) {
        result.delayedBallUpdate = {
          allBalls: gameData.allBalls,
          delay: 4000, // 4 secondes après le message de boule frimée
        };
      }
      break;

    default:
      // Autres messages
      if (messageId) {
        playAudio(messageId);
        result.audioToPlay = messageId;
        result.messageToDisplay = {
          message: showMessage(messageId),
          status: 'warning',
          show: true,
        };
      }
      break;
  }

  return result;
}

/**
 * Traite les messages personnalisés
 */
function handlePersonalizedMessage(gameData) {
  return {
    needsFullRefresh: false,
    messageToDisplay: {
      message: gameData.message || '',
      status: 'warning',
      show: true,
    },
  };
}

/**
 * Traite l'action "winner" (gagnant)
 */
function handleWinnerAction(gameData, callbacks) {
  console.info('🏆 handleWinnerAction appelé - Message:', gameData.message, '| TypeTirages:', gameData.typeTirages);

  const result = {
    needsFullRefresh: false,
    updatedModel: null,
    audioToPlay: null,
    messageToDisplay: null,
  };

  // Jouer l'audio du gagnant
  console.log('🔊 Appel playAudio pour:', gameData.message);
  playAudio(gameData.message);
  result.audioToPlay = gameData.message;

  // Préparer le message à afficher
  const messageText = showMessage(gameData.message);
  result.messageToDisplay = {
    message: messageText,
    status: 'info',
    show: true,
  };

  // Gestion spéciale pour "MemeCarte"
  if (
    gameData.typeTirages &&
    gameData.typeTirages > '' &&
    gameData.message !== GAME_MESSAGES.GAME_STARTS_AGAIN
  ) {
    window.audioFilenames = 'MemeCarte,';
    playAudio('MemeCarte');
  }

  // Mise à jour incrémentale si possible
  if (callbacks.currentModel && canUpdateIncrementally(GAME_ACTIONS.WINNER)) {
    result.updatedModel = updateGameState(callbacks.currentModel, gameData);
  }

  return result;
}

/**
 * Traite l'action "updateWinner" (mise à jour des gagnants)
 */
function handleUpdateWinnerAction(gameData, callbacks) {
  const result = {
    needsFullRefresh: false,
    updatedModel: null,
    messageToDisplay: {
      message: 'Il y a une modification dans les gagnants',
      status: 'warning',
      show: true,
    },
  };

  // Mise à jour incrémentale
  if (
    callbacks.currentModel &&
    canUpdateIncrementally(GAME_ACTIONS.UPDATE_WINNER)
  ) {
    result.updatedModel = updateGameState(callbacks.currentModel, gameData);
  }

  return result;
}

/**
 * Traite les actions par défaut
 */
function handleDefaultAction(gameData) {
  const result = {
    needsFullRefresh: false,
    messageToDisplay: null,
    audioToPlay: null,
  };

  if (gameData.message) {
    const messageText = showMessage(gameData.message);
    result.messageToDisplay = {
      message: messageText,
      status: 'info',
      show: true,
    };
  }

  if (gameData.audioMessage) {
    playAudio(gameData.audioMessage);
    result.audioToPlay = gameData.audioMessage;
  }

  return result;
}

